import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useOffersStore } from '@/stores/offersStore';
import StudyOfferCard from '@/components/study/StudyOfferCard';
import { useAuth } from '@/contexts/AuthContext';
import { useAuthStore } from '@/stores';

interface Props {
  className?: string;
}

const StudyOfferNotification: React.FC<Props> = ({ className }) => {
  const {
    offers,
    unreadCount,
    isOpen,
    setOpen,
    accept,
    startRealtime,
    getDistanceKm,
  } = useOffersStore();
  const { user } = useAuth();
  const { activeRegionId } = useAuthStore();
  const { pathname } = useLocation();

  useEffect(() => {
    // Only start realtime on the Dashboard (home) or when the bell is opened
    const shouldAutoStart = pathname === '/' || pathname === '/dashboard';
    const regionId = (user?.region_id ?? activeRegionId) ?? null;
    if (!regionId) return;
    if (shouldAutoStart || isOpen) {
      void startRealtime(regionId);
    }
    // Re-run when route changes, region hydrates, or panel opens
  }, [startRealtime, user?.region_id, activeRegionId, pathname, isOpen]);

  // Only render the bell when there are offers to show
  if ((offers?.length || 0) === 0) {
    return null;
  }

  return (
    <div className={`relative ${className || ''}`}>
      <>
        <button
          type="button"
          className="relative p-1 rounded-full text-yellow-500 hover:text-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          onClick={() => setOpen(!isOpen)}
          title="Ofertas de Agendamento"
        >
          <span className="sr-only">Ver ofertas de agendamento</span>
          <Bell className="h-6 w-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-emerald-600 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-[22rem] bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
            <div className="p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Ofertas de Agendamento</h3>
              {offers.length === 0 ? (
                <div className="text-sm text-gray-600">Sem ofertas ativas para a sua região.</div>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {offers.map((offer) => (
                    <StudyOfferCard
                      key={offer.id}
                      offer={offer}
                      onAccept={accept}
                      getDistanceKm={getDistanceKm}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </>
    </div>
  );
};

export default StudyOfferNotification;
